import React from 'react';

// A helper function to generate random values within a range
const randomBetween = (min, max) => Math.random() * (max - min) + min;

/**
 * A 3D loader where astronomical objects float randomly around static text.
 */
const FloatingObjectsLoader = () => {
  const text = "Afrin Jewelry";
  const objectCount = text.trim().length();

  // --- Generate styles and animations for each object ---
  // Using React.useMemo to ensure styles are generated only once per component mount
  const generatedObjects = React.useMemo(() => {
    const objects = [];
    let allKeyframes = '';

    for (let i = 0; i < objectCount; i++) {
      const size = randomBetween(15, 35);
      const duration = randomBetween(20, 35);
      const delay = randomBetween(0, 20);
      const animationName = `float-${i}`;

      const keyframes = `
        @keyframes ${animationName} {
          0% { transform: translate3d(${randomBetween(-180, 180)}px, ${randomBetween(-180, 180)}px, ${randomBetween(-250, 150)}px) rotateX(0deg) rotateY(0deg); }
          50% { transform: translate3d(${randomBetween(-220, 220)}px, ${randomBetween(-220, 220)}px, ${randomBetween(-250, 250)}px) rotateX(${randomBetween(-180, 180)}deg) rotateY(${randomBetween(-180, 180)}deg); }
          100% { transform: translate3d(${randomBetween(-180, 180)}px, ${randomBetween(-180, 180)}px, ${randomBetween(-250, 150)}px) rotateX(360deg) rotateY(360deg); }
        }
      `;
      allKeyframes += keyframes;

      const animationStyle = {
        position: 'absolute',
        width: `${size}px`,
        height: `${size}px`,
        transformStyle: 'preserve-3d',
        animation: `${animationName} ${duration}s infinite alternate ease-in-out`,
        animationDelay: `${delay}s`,
      };

      const objectType = Math.floor(randomBetween(0, 4));
      let jsx;

      switch (objectType) {
        // Ringed Planet
        case 0:
          jsx = (
            <div key={i} style={animationStyle}>
              <div style={{
                position: 'absolute', width: '100%', height: '100%', borderRadius: '50%',
                background: 'radial-gradient(circle at 30% 30%, #fde047, #f59e0b)',
                transform: 'translateZ(-2px)'
              }} />
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                width: `${size * 2}px`, height: `${size * 2}px`,
                border: `${Math.max(2, size / 8)}px solid #d8c0a0`,
                borderRadius: '50%',
                transform: `translate(-50%, -50%) rotateX(80deg) rotateZ(20deg)`,
              }} />
            </div>
          );
          break;
        
        // Satellite
        case 1:
          jsx = (
            <div key={i} style={{ ...animationStyle, width: `${size * 0.7}px`, height: `${size * 0.7}px` }}>
              <div style={{
                position: 'absolute', width: '100%', height: '100%', background: '#9e9e9e',
                boxShadow: 'inset 0 0 5px #424242', transform: 'rotateY(45deg) rotateX(10deg)'
              }} />
              <div style={{
                position: 'absolute', top: '50%', left: `-${size * 1.5}px`, width: `${size * 2}px`, height: '70%',
                background: 'linear-gradient(45deg, #1e3a8a, #3b82f6)', transform: 'translateY(-50%)',
              }} />
              <div style={{
                position: 'absolute', top: '50%', right: `-${size * 1.5}px`, width: `${size * 2}px`, height: '70%',
                background: 'linear-gradient(-45deg, #1e3a8a, #3b82f6)', transform: 'translateY(-50%)',
              }} />
            </div>
          );
          break;
          
        // Red Planet
        case 2:
          jsx = (
            <div key={i} style={animationStyle}>
              <div style={{
                width: '100%', height: '100%', borderRadius: '50%',
                background: 'radial-gradient(circle at 20% 20%, #ffccbc, #ff5722, #bf360c)',
              }} />
            </div>
          );
          break;
          
        // Moon
        default:
          jsx = (
            <div key={i} style={animationStyle}>
              <div style={{
                width: '100%', height: '100%', borderRadius: '50%',
                background: 'radial-gradient(circle at 30% 30%, #E0E0E0, #A0A0A0, #616161)',
                boxShadow: `0 0 ${size / 2}px #BDBDBD`,
              }} />
            </div>
          );
          break;
      }
      objects.push({ jsx, keyframes });
    }
    return { objects, allKeyframes };
  }, []); // Empty dependency array means this runs only once

  const objectElements = generatedObjects.objects.map(o => o.jsx);
  const allKeyframes = generatedObjects.allKeyframes;

  const sceneStyle = {
    position: 'relative', width: '500px', height: '500px', perspective: '1200px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  
  const textStyle = {
    fontSize: '2.5rem', fontWeight: 'bold', fontFamily: 'sans-serif', color: '#f0f9ff',
    textShadow: `0 0 5px #60a5fa, 0 0 10px #60a5fa, 0 0 20px #60a5fa`,
    transformStyle: 'preserve-3d', zIndex: 10,
  };
  
  const objectsContainerStyle = {
      position: 'absolute', width: '100%', height: '100%', transformStyle: 'preserve-3d',
  };

  const dotStyle = (index) => ({
    animation: `blink 1.5s infinite`,
    animationDelay: `${index * 0.3}s`,
  });

  return (
    <>
      <style>
        {`
          ${allKeyframes}
          @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
        `}
      </style>
      <div style={sceneStyle}>
        <div style={textStyle}>
          {text}
          <span style={dotStyle(0)}>.</span>
          <span style={dotStyle(1)}>.</span>
          <span style={dotStyle(2)}>.</span>
        </div>
        <div style={objectsContainerStyle}>
           {objectElements}
        </div>
      </div>
    </>
  );
};

export default function Loader() {
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white font-sans overflow-hidden">
      <div className="w-auto h-64 flex items-center justify-center">
        <FloatingObjectsLoader />
      </div>
    </div>
  );
}

